// Setup › Decisions (`/c/{slug}/setup/decisions`; B5-DECIDE). The cheap classifier a DE calls to
// sort many items: managers turn Jev on OpenRouter and the board gate on or off (saved at once,
// `PUT …/decide/settings`) and see what a call uses now and whether the company holds an
// OpenRouter key. Everyone who may view a DE's runs reads its rubrics (`GET …/decision-rubrics`):
// View opens the questions and the version list in a modal, Edit a modal editor whose save is a
// new version (`POST …/decision-rubrics/{id}/versions`). Keys and question structure stay the DE's.
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Notice,
  SettingsSection,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  ToggleRow,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { EyeIcon, ListChecksIcon, PencilIcon, ScaleIcon, ToggleRightIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isManager } from "../company";
import { peopleQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import {
  type DecideSettings,
  type DecideSettingsUpdate,
  decideKeys,
  decideSettingsQuery,
  draftOfRubric,
  editOf,
  pct,
  type Rubric,
  type RubricDraft,
  rubricQuery,
  rubricsQuery,
} from "./queries";

export function DecisionsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const manager = isManager(company);
  return (
    <SetupPage title="Decisions" slot="setup-decisions">
      {manager ? <SwitchesCard api={api} company={company} /> : null}
      <RubricsCard api={api} company={company} manager={manager} />
    </SetupPage>
  );
}

// ── Switches ────────────────────────────────────────────────────────────────

function Label({ text, tip }: { text: string; tip: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      {text}
      <InfoTip label={`About ${text}`}>{tip}</InfoTip>
    </span>
  );
}

/** A field label with its (i) beside it (outside the `<label>`, so the field's name stays short). */
function TipLabel({ htmlFor, text, tip }: { htmlFor: string; text: string; tip: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      <FieldLabel htmlFor={htmlFor}>{text}</FieldLabel>
      <InfoTip label={`About ${text}`}>{tip}</InfoTip>
    </span>
  );
}

const USES: Record<string, string> = { jev: "Jev", local: "Local model" };

function SwitchesCard({ api, company }: { api: Api; company: CompanyAccess }) {
  const qc = useQueryClient();
  const q = useQuery(decideSettingsQuery(api, company.id));
  const save = useMutation({
    mutationFn: (body: DecideSettingsUpdate) =>
      api.client.decide.settings.update({ params: { co: company.id }, body }),
    onSuccess: (v) => {
      qc.setQueryData(decideKeys.settings(company.id), v);
      toast.add({ title: "Saved", type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not saved."), type: "error" }),
  });
  return (
    <SettingsSection id="switches" icon={ToggleRightIcon} title="Switches">
      {q.isPending ? (
        <Loading label="Loading the switches" rows={2} />
      ) : q.isError ? (
        <LoadError what="The switches" error={q.error} />
      ) : (
        <SwitchesBody
          s={q.data}
          slug={company.slug}
          saving={save.isPending}
          onSave={(b) => save.mutate(b)}
        />
      )}
    </SettingsSection>
  );
}

function SwitchesBody({
  s,
  slug,
  saving,
  onSave,
}: {
  s: DecideSettings;
  slug: string;
  saving: boolean;
  onSave: (b: DecideSettingsUpdate) => void;
}) {
  return (
    <>
      <ToggleRow
        label={
          <Label
            text="Jev on OpenRouter"
            tip={`Sends the item text, with e-mail addresses and phone numbers masked, to ${s.jev_model} on OpenRouter. Off: only the company's own light model classifies. Files are never sent.`}
          />
        }
        checked={s.external}
        disabled={saving}
        onCheckedChange={(on) => onSave({ external: on })}
      />
      <ToggleRow
        label={
          <Label
            text="Board gate"
            tip="The classifier may re-class a DE-owned board's urgent comments and batch edits before they wake the DE."
          />
        }
        checked={s.board_gate}
        disabled={saving}
        onCheckedChange={(on) => onSave({ board_gate: on })}
      />
      <dl
        className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 border-t border-subtle pt-4 text-sm"
        data-slot="decide-status"
      >
        <dt className="text-muted-foreground">Uses</dt>
        <dd data-slot="decide-uses">
          <Badge variant={s.provider ? "success" : "outline"} dot={Boolean(s.provider)}>
            {s.provider ? (USES[s.provider] ?? s.provider) : "None"}
          </Badge>
        </dd>
        <dt className="text-muted-foreground">Key</dt>
        <dd data-slot="decide-key">
          {s.openrouter_key.present ? (
            <span className="font-mono text-xs">
              OpenRouter key ••••{s.openrouter_key.last4 ?? ""}
            </span>
          ) : (
            <span>
              No OpenRouter key ·{" "}
              <Link to="/c/$slug/setup/connections" params={{ slug }} className="underline">
                Keys & connections
              </Link>
            </span>
          )}
        </dd>
      </dl>
    </>
  );
}

// ── Rubrics ─────────────────────────────────────────────────────────────────

type Open = { kind: "view" | "edit"; rubric: Rubric } | null;

function RubricsCard({
  api,
  company,
  manager,
}: {
  api: Api;
  company: CompanyAccess;
  manager: boolean;
}) {
  const q = useQuery(rubricsQuery(api, company.id));
  const people = useInfiniteQuery({ ...peopleQuery(api, company.id), enabled: manager });
  const names = new Map(
    (people.data?.pages.flatMap((p) => p.data) ?? []).map((p) => [p.id, p.display_name]),
  );
  const [open, setOpen] = useState<Open>(null);
  const savedBy = (r: Rubric) =>
    r.saved_by.kind === "digital_employee" ? (
      <DeName name={r.agent_name ?? "DE"} />
    ) : (
      ((r.saved_by.membership_id && names.get(r.saved_by.membership_id)) ?? "Person")
    );
  const rows = q.data?.data ?? [];
  return (
    <SettingsSection
      id="rubrics"
      icon={ListChecksIcon}
      title={
        <Label
          text="Rubrics"
          tip="The questions a DE asks the classifier, with what each answer is for. Your edit saves a new version."
        />
      }
    >
      {q.isPending ? (
        <Loading label="Loading the rubrics" rows={3} />
      ) : q.isError ? (
        <LoadError what="The rubrics" error={q.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={ScaleIcon}
          title="No rubrics yet"
          description="A DE writes its own when it starts using decide."
        />
      ) : (
        <Table data-slot="decide-rubrics" className={DENSE_TABLE}>
          <TableCaption className="sr-only">The DEs' classifier rubrics.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">DE</TableHead>
              <TableHead scope="col">Rubric</TableHead>
              <TableHead scope="col">Version</TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Act / review
              </TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Answers
              </TableHead>
              <TableHead scope="col" className="max-sm:hidden">
                Saved by
              </TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-rubric={r.key} className="group/row">
                <TableCell>
                  <DeName name={r.agent_name ?? "—"} />
                </TableCell>
                <TableHead scope="row" className="font-medium">
                  {r.title}
                  <span className="ml-2 font-mono text-xs font-normal text-muted-foreground">
                    {r.key}
                  </span>
                </TableHead>
                <TableCell className="tabular-nums" data-slot="rubric-version">
                  v{r.version}
                </TableCell>
                <TableCell className="tabular-nums text-muted-foreground max-md:hidden">
                  {pct(r.act_threshold)} / {pct(r.review_threshold)}
                </TableCell>
                <TableCell className="tabular-nums text-muted-foreground max-md:hidden">
                  {r.decisions}
                </TableCell>
                <TableCell className="text-muted-foreground max-sm:hidden">{savedBy(r)}</TableCell>
                <TableCell className="w-px text-right">
                  <RowActions>
                    <RowAction
                      icon={EyeIcon}
                      label={`View ${r.title}`}
                      tip="View"
                      onClick={() => setOpen({ kind: "view", rubric: r })}
                    />
                    <RowAction
                      icon={PencilIcon}
                      label={`Edit ${r.title}`}
                      tip="Edit"
                      onClick={() => setOpen({ kind: "edit", rubric: r })}
                    />
                  </RowActions>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {open?.kind === "view" ? (
        <ViewDialog
          api={api}
          company={company}
          rubric={open.rubric}
          onClose={() => setOpen(null)}
        />
      ) : open?.kind === "edit" ? (
        <EditDialog
          api={api}
          company={company}
          rubric={open.rubric}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </SettingsSection>
  );
}

function DeName({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {name}
      <Badge variant="accent" className="px-1 text-[10px]">
        DE
      </Badge>
    </span>
  );
}

function ViewDialog({
  api,
  company,
  rubric,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  rubric: Rubric;
  onClose: () => void;
}) {
  const q = useQuery(rubricQuery(api, company.id, rubric.id));
  const r = q.data ?? rubric;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[640px]" data-slot="rubric-view">
        <DialogHeader>
          <DialogTitle>
            {r.title} <span className="text-muted-foreground">v{r.version}</span>
          </DialogTitle>
          <DialogDescription>
            {r.agent_name ?? "DE"} · <span className="font-mono">{r.key}</span> · act at{" "}
            {pct(r.act_threshold)}, review at {pct(r.review_threshold)}
          </DialogDescription>
        </DialogHeader>
        {r.focus ? <p className="text-sm">{r.focus}</p> : null}
        <div className="flex flex-col gap-4">
          {r.questions.map((qn) => (
            <section key={qn.key} className="flex flex-col gap-2">
              <h3 className="text-sm font-medium">
                {qn.question}{" "}
                <span className="font-mono text-xs font-normal text-muted-foreground">
                  {qn.key} · {qn.type}
                </span>
              </h3>
              {qn.focus ? <p className="text-xs text-muted-foreground">{qn.focus}</p> : null}
              <ul className="flex flex-col gap-1.5 text-sm">
                {qn.options.map((o) => (
                  <li key={o.key} className="rounded-md border border-subtle px-3 py-2">
                    <span className="font-mono text-xs text-muted-foreground">{o.key}</span>
                    <p>{o.what}</p>
                    {o.not_for ? (
                      <p className="text-xs text-muted-foreground">Not for: {o.not_for}</p>
                    ) : null}
                    {o.examples?.length ? (
                      <p className="text-xs text-muted-foreground">
                        e.g. {o.examples.map((x) => `"${x}"`).join(", ")}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <section className="flex flex-col gap-1.5" data-slot="rubric-versions">
          <h3 className="text-sm font-medium">Versions</h3>
          {q.isPending ? (
            <Loading label="Loading the versions" rows={2} />
          ) : q.isError ? (
            <LoadError what="The versions" error={q.error} />
          ) : (
            <ul className="flex flex-col text-sm">
              {q.data.versions.map((v) => (
                <li key={v.id} className="flex gap-3 border-b border-subtle py-1.5 last:border-b-0">
                  <span className="w-8 tabular-nums">v{v.version}</span>
                  <span className="text-muted-foreground">
                    {v.saved_by.kind === "digital_employee" ? "DE" : "Person"}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{v.note ?? ""}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {v.created_at.slice(0, 10)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  api,
  company,
  rubric,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  rubric: Rubric;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const id = useId();
  const [d, setD] = useState<RubricDraft>(() => draftOfRubric(rubric));
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: Parameters<typeof api.client.decisionRubrics.edit>[0]["body"]) =>
      api.client.decisionRubrics.edit({ params: { co: company.id, id: rubric.id }, body }),
    onSuccess: (v) => {
      qc.setQueryData(decideKeys.rubrics(company.id), (old: { data: Rubric[] } | undefined) =>
        old ? { data: old.data.map((x) => (x.id === rubric.id ? v : x)) } : old,
      );
      void qc.invalidateQueries({ queryKey: decideKeys.rubrics(company.id) });
      toast.add({ title: `${v.title} saved as v${v.version}`, type: "success" });
      onClose();
    },
  });
  const set = (patch: Partial<RubricDraft>) => {
    setProblem(null);
    save.reset();
    setD((x) => ({ ...x, ...patch }));
  };
  const setOpt = (
    qi: number,
    oi: number,
    patch: Partial<RubricDraft["questions"][0]["options"][0]>,
  ) =>
    set({
      questions: d.questions.map((q, i) =>
        i !== qi
          ? q
          : { ...q, options: q.options.map((o, j) => (j === oi ? { ...o, ...patch } : o)) },
      ),
    });
  const setQ = (qi: number, patch: Partial<Omit<RubricDraft["questions"][0], "options">>) =>
    set({ questions: d.questions.map((q, i) => (i === qi ? { ...q, ...patch } : q)) });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const out = editOf(rubric, d);
    if ("problem" in out) setProblem(out.problem);
    else save.mutate(out.body);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-[680px]" data-slot="rubric-edit">
        <form
          onSubmit={onSubmit}
          noValidate
          className="flex flex-col gap-5"
          aria-label="Edit rubric"
        >
          <DialogHeader>
            <DialogTitle>Edit {rubric.title}</DialogTitle>
            <DialogDescription>
              {rubric.agent_name ?? "DE"} · <span className="font-mono">{rubric.key}</span> · v
              {rubric.version} → v{rubric.version + 1}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-title`}>Title</FieldLabel>
            <Input
              id={`${id}-title`}
              value={d.title}
              onChange={(e) => set({ title: e.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-focus`}>Focus</FieldLabel>
            <Input
              id={`${id}-focus`}
              value={d.focus}
              onChange={(e) => set({ focus: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <TipLabel
                htmlFor={`${id}-act`}
                text="Act at"
                tip="At or above this confidence the DE acts on the answer."
              />
              <Input
                id={`${id}-act`}
                type="number"
                inputMode="decimal"
                step="0.05"
                min="0.05"
                max="1"
                value={d.act}
                onChange={(e) => set({ act: e.target.value })}
              />
            </Field>
            <Field>
              <TipLabel
                htmlFor={`${id}-review`}
                text="Review at"
                tip="From here up to Act at the DE reads the item itself; below it asks a person."
              />
              <Input
                id={`${id}-review`}
                type="number"
                inputMode="decimal"
                step="0.05"
                min="0.05"
                max="0.95"
                value={d.review}
                onChange={(e) => set({ review: e.target.value })}
              />
            </Field>
          </div>
          {rubric.questions.map((qn, qi) => (
            <fieldset
              key={qn.key}
              className="flex flex-col gap-3 rounded-lg border border-subtle p-3"
              data-question={qn.key}
            >
              <legend className="px-1 font-mono text-xs text-muted-foreground">
                {qn.key} · {qn.type}
              </legend>
              <Field>
                <FieldLabel htmlFor={`${id}-${qn.key}-q`}>Question</FieldLabel>
                <Input
                  id={`${id}-${qn.key}-q`}
                  value={d.questions[qi]?.question ?? ""}
                  onChange={(e) => setQ(qi, { question: e.target.value })}
                />
              </Field>
              {qn.options.map((o, oi) => {
                const v = d.questions[qi]?.options[oi];
                const base = `${id}-${qn.key}-${oi}`;
                return (
                  <div key={o.key} className="flex flex-col gap-2 border-t border-subtle pt-3">
                    <span className="font-mono text-xs text-muted-foreground">{o.key}</span>
                    <Field>
                      <FieldLabel htmlFor={`${base}-what`}>What</FieldLabel>
                      <Textarea
                        id={`${base}-what`}
                        aria-label={`What: ${qn.key} › ${o.key}`}
                        rows={2}
                        value={v?.what ?? ""}
                        onChange={(e) => setOpt(qi, oi, { what: e.target.value })}
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor={`${base}-not`}>Not for</FieldLabel>
                      <Input
                        id={`${base}-not`}
                        aria-label={`Not for: ${qn.key} › ${o.key}`}
                        value={v?.not_for ?? ""}
                        onChange={(e) => setOpt(qi, oi, { not_for: e.target.value })}
                      />
                    </Field>
                    <Field>
                      <TipLabel
                        htmlFor={`${base}-ex`}
                        text="Examples"
                        tip="One per line, at most 8."
                      />
                      <Textarea
                        id={`${base}-ex`}
                        aria-label={`Examples: ${qn.key} › ${o.key}`}
                        rows={2}
                        value={v?.examples ?? ""}
                        onChange={(e) => setOpt(qi, oi, { examples: e.target.value })}
                      />
                    </Field>
                  </div>
                );
              })}
            </fieldset>
          ))}
          <Field>
            <FieldLabel htmlFor={`${id}-note`}>Note</FieldLabel>
            <Input
              id={`${id}-note`}
              value={d.note}
              placeholder="What changed"
              onChange={(e) => set({ note: e.target.value })}
            />
          </Field>
          {problem ? (
            <Notice tone="danger" role="alert">
              {problem}
            </Notice>
          ) : save.isError ? (
            <Notice tone="danger" role="alert">
              {problemText(save.error, "The rubric was not saved.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              Save as v{rubric.version + 1}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
